import React, { useState, useEffect, useRef } from 'react';
import { FiSettings, FiCheckCircle, FiXCircle, FiUser, FiMail, FiShield, FiLink, FiDollarSign, FiLock, FiRefreshCw, FiCamera, FiSave } from 'react-icons/fi';
import { DashboardData } from '../../types';
import { useApi } from '../../hooks/useApi';
import { socketService } from '../../services/socketService';
import { showToast } from '../../utils/toast';
import { isSubscriptionAccessAllowed, isMasterFeeOnlyPlan, isComplimentaryPlan } from '../../utils/subscriptionAccess';
import { getProfileImageUrl, DEFAULT_AVATAR_URL } from '../../utils/imageUtils';
import { Loader, LoadingButton } from '../Common/Loader';
interface SettingsProps {
  data: DashboardData | null;
  refresh: () => void;
}
const Settings: React.FC<SettingsProps> = ({ data, refresh }) => {
  const [name, setName] = useState<string>(data?.user?.name || '');
  const [allocation, setAllocation] = useState<number>(data?.user?.allocationPercent || 10);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(data?.user?.profileImage || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync state if data changes
  useEffect(() => {
    console.log(data)
    if (data?.user) {
      setName(data.user.name);
      setAllocation(data.user.allocationPercent || 10);
      setImagePreview(data.user.profileImage || null);
    }
  }, [data?.user]);

  // Only fetch subscription data for follower users (not admin, master, or pending master)
  const isAdmin = data?.user?.role === 'admin';
  const isMaster = data?.user?.role === 'master' || data?.user?.role === 'master_pending';
  const isLegacyPlan = isMasterFeeOnlyPlan(data?.user?.plan) || isComplimentaryPlan(data?.user?.plan);
  // og / old_user / if&brice_user: no platform subscription UI (master fee only)
  const shouldShowSubscription = !isAdmin && !isMaster && !isLegacyPlan;

  const { data: subscriptionData } = useApi.useSubscriptionStatus({
    enabled: shouldShowSubscription,
  });
  const createCheckoutSessionMutation = useApi.useCreateCheckoutSession();

  const updateProfileMutation = useApi.useUpdateProfile({
    onSuccess: () => {
      showToast('success', 'Profile updated successfully');
      refresh();
    },
    onError: (error: any) => {
      showToast('error', error.response?.data?.error || 'Failed to update profile');
    }
  });

  const hasActiveSubscription = isSubscriptionAccessAllowed(subscriptionData);

  // Listen for subscription updates via WebSocket
  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    const handleSubscriptionUpdate = (data: any) => {
      console.log('💳 Subscription update received in Settings:', data);
    };

    socket.on('subscription:update', handleSubscriptionUpdate);

    return () => {
      socket.off('subscription:update', handleSubscriptionUpdate);
    };
  }, []);

  const handleUpdateAllocation = async (): Promise<void> => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/dashboard/copy-trading/allocation`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ allocationPercent: allocation })
        }
      );

      const result = await response.json();

      if (response.ok) {
        showToast('success', 'Allocation updated successfully');
        refresh();
      } else {
        showToast('error', result.error || 'Failed to update allocation');
      }
    } catch (error) {
      showToast('error', 'Failed to update allocation');
    }
  };

  const handleUpdateProfile = () => {
    const formData = new FormData();
    formData.append('name', name);
    if (profileImage) {
      formData.append('profileImage', profileImage);
    }

    updateProfileMutation.mutate(formData);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        showToast('error', 'Image size must be less than 5MB');
        return;
      }
      setProfileImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };



  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center gap-3">
        <div className="bg-primary/10 lg:p-2 p-1 rounded-xl">
          <FiSettings className="h-7 w-7 text-primary" />
        </div>
        <h2 className="lg:text-3xl text-xl font-bold text-foreground">Settings</h2>
      </div>

      {/* Profile Section */}
      <div className="card-premium">
        <h3 className="text-lg sm:text-xl font-bold text-foreground mb-6 flex items-center gap-2">
          <FiUser className="h-5 w-5 text-primary" />
          Profile Settings
        </h3>

        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
          <div className="relative group">
            <div className="w-32 h-32 rounded-2xl overflow-hidden border-4 border-surface shadow-xl bg-surface-elevated">
              <img
                src={getProfileImageUrl(imagePreview)}
                alt="Profile"
                crossOrigin="anonymous"
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = DEFAULT_AVATAR_URL;
                }}
              />
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute -bottom-2 -right-2 bg-primary p-2.5 rounded-xl text-white shadow-lg hover:bg-primary-600 transition-all hover:scale-110 active:scale-95"
            >
              <FiCamera className="w-5 h-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleImageChange}
            />
          </div>

          <div className="flex-1 w-full min-w-0 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-muted ml-1">Full Name</label>
              <div className="relative">
                <FiUser className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-surface border-2 border-border rounded-xl focus:border-primary focus:outline-none transition-all"
                  placeholder="Your full name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-muted ml-1">Email Address</label>
              <div className="relative">
                <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
                <input
                  type="email"
                  value={data?.user?.email || ''}
                  disabled
                  className="w-full pl-11 pr-4 py-3 bg-muted/10 border-2 border-border rounded-xl text-muted cursor-not-allowed"
                />
              </div>
              <p className="text-xs text-muted ml-1">Email address cannot be changed</p>
            </div>

            <div className="pt-2">
              <LoadingButton
                onClick={handleUpdateProfile}
                isLoading={updateProfileMutation.isPending}
                loadingText="Saving Changes..."
                icon={<FiSave className="w-4 h-4" />}
                className="w-full md:w-auto px-8"
              >
                Save Profile Changes
              </LoadingButton>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Status - Only for follower users */}
      {shouldShowSubscription && (
        <div className={`card-premium ${hasActiveSubscription ? 'border-primary/20' : 'border-warning/20'}`}>
          <h3 className="text-lg sm:text-xl font-bold text-foreground mb-6 flex items-center gap-2">
            <FiDollarSign className="h-5 w-5 text-primary" />
            Subscription Status
          </h3>
          {hasActiveSubscription && subscriptionData?.subscription ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-success bg-success/10 p-3 rounded-xl border border-success/20">
                <FiCheckCircle className="h-5 w-5" />
                <span className="font-bold uppercase tracking-wide text-xs">Active Subscription</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-surface rounded-xl border border-border">
                  <span className="text-xs font-bold text-muted uppercase tracking-wider">Current Plan</span>
                  <p className="font-bold text-lg text-foreground mt-1">
                    {subscriptionData.subscription.billingMode === 'trial'
                      ? `${(subscriptionData.subscription.plan || 'general').charAt(0).toUpperCase()}${(subscriptionData.subscription.plan || 'general').slice(1)} trial`
                      : `${(subscriptionData.subscription.plan || subscriptionData.subscription.planType || 'general').charAt(0).toUpperCase()}${(subscriptionData.subscription.plan || subscriptionData.subscription.planType || 'general').slice(1)}`}
                  </p>
                </div>
                <div className="p-4 bg-surface rounded-xl border border-border">
                  <span className="text-xs font-bold text-muted uppercase tracking-wider">Days Remaining</span>
                  <p className="font-bold text-lg text-foreground mt-1">
                    {subscriptionData.subscription.daysRemaining} Days
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 p-4 bg-warning/5 rounded-2xl border border-warning/10">
              <div className="flex items-center gap-2 text-warning">
                <FiLock className="h-5 w-5" />
                <span className="font-bold">Access Restricted</span>
              </div>
              <p className="text-muted">
                Subscribe to connect your brokerage account and start copy trading from master traders.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Account Info */}
      <div className="card-premium">
        <h3 className="text-lg sm:text-xl font-bold text-foreground mb-6 flex items-center gap-2">
          <FiShield className="h-5 w-5 text-primary" />
          System Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="p-4 bg-surface rounded-xl border border-border">
            <span className="text-xs font-bold text-muted uppercase tracking-wider block mb-1">User Role</span>
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-primary/10 rounded-lg">
                <FiShield className="w-4 h-4 text-primary" />
              </div>
              <span className="font-bold text-foreground capitalize">{data?.user?.role}</span>
            </div>
          </div>

          <div className="p-4 bg-surface rounded-xl border border-border">
            <span className="text-xs font-bold text-muted uppercase tracking-wider block mb-1">Brokerage Status</span>
            <div className="flex items-center gap-2">
              {data?.user?.hasBrokerageConnected ? (
                <>
                  <div className="p-1.5 bg-success/10 rounded-lg">
                    <FiLink className="w-4 h-4 text-success" />
                  </div>
                  <span className="font-bold text-success">Connected</span>
                </>
              ) : (
                <>
                  <div className="p-1.5 bg-danger/10 rounded-lg">
                    <FiXCircle className="w-4 h-4 text-danger" />
                  </div>
                  <span className="font-bold text-danger">Disconnected</span>
                </>
              )}
            </div>
          </div>

          {/* <div className="p-4 bg-surface rounded-xl border border-border">
            <span className="text-xs font-bold text-muted uppercase tracking-wider block mb-1">Trading Type</span>
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-accent-500/10 rounded-lg">
                <FiRefreshCw className="w-4 h-4 text-accent-500" />
              </div>
              <span className="font-bold text-foreground capitalize">{data?.user?.tradingType || 'Standard'}</span>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default Settings;
